/**
 * 服务器时钟偏移同步
 *
 * 场景：服务端会校验请求 token 中的时间戳 v（容差 ±60 秒），手机系统时间不准时
 *       返回 304 timestamp expired，以前只能弹窗让用户手动去改手机时间。
 *
 * 思路（对齐 AWS SDK 的 correctClockSkew / systemClockOffset）：
 *   1. 不相信本机时钟，而是维护一个偏移量 skewMs = 服务器时间 - 本机时间；
 *   2. 偏移量从 HTTP 响应的 Date 头推算（Date 属于 CORS 安全响应头，无需额外 expose）；
 *   3. 生成时间戳时统一使用 Date.now() + skewMs；
 *   4. 服务端判定时间戳过期时，先用 Date 头纠正偏移，再自动重试一次，用户无感；
 *      只有 Date 头缺失、或重试后仍然过期（说明不是本机时钟问题）时，
 *      才回退到原有的「手动调整手机时间」弹窗。
 *
 * 精度说明：HTTP Date 只精确到秒（服务器时间被截断到整秒），取截断区间中值并补偿
 *          半个 RTT，绝对误差在 1 秒内，远小于服务端 60 秒容差，足够使用。
 */

const STORAGE_KEY = 'dcops_clock_skew_ms';
// 偏移量的有效期：手机时间一般不会频繁变化，刷新页面后可直接复用；超时则重新校准
const STORAGE_TTL = 12 * 60 * 60 * 1000;
// 超过 7 天的偏差基本可以判定为脏数据，不采纳
const MAX_SKEW = 7 * 24 * 60 * 60 * 1000;
// 已有基准时，偏差小于该阈值不更新，避免 Date 头秒级精度带来的反复抖动
const MIN_UPDATE_DELTA = 1000;

/** 服务器时间与本机时间的差值（毫秒） */
let skewMs = 0;
/** 是否已经从服务器校准过基准 */
let calibrated = false;

function getStorage() {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch (e) {
    return null;
  }
}

function restore() {
  const storage = getStorage();
  if (!storage) return;
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return;
    const { skew, savedAt } = JSON.parse(raw) || {};
    if (!Number.isFinite(skew) || !Number.isFinite(savedAt)) return;
    const now = Date.now();
    // savedAt 落在未来说明本机时间被回拨过，或已超期：直接丢弃，等下一次 Date 头校准
    if (savedAt > now || now - savedAt > STORAGE_TTL) return;
    if (Math.abs(skew) > MAX_SKEW) return;
    skewMs = skew;
    calibrated = true;
  } catch (e) {
    // 解析失败忽略，按未校准处理
  }
}

function persist() {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify({ skew: skewMs, savedAt: Date.now() }));
  } catch (e) {
    // sessionStorage 不可用（隐私模式/配额满）时仅内存生效
  }
}

function apply(newSkew) {
  if (!Number.isFinite(newSkew) || Math.abs(newSkew) > MAX_SKEW) return false;
  if (calibrated && Math.abs(newSkew - skewMs) < MIN_UPDATE_DELTA) {
    // 偏差很小，保持已有基准，避免抖动
    return true;
  }
  skewMs = Math.round(newSkew);
  calibrated = true;
  persist();
  return true;
}

restore();

/**
 * 用响应头里的 Date 校准时钟基准
 * @param {string} dateHeader 响应头 Date（GMT 格式字符串）
 * @param {number} startTime 请求发出时的本机时间戳
 * @param {number} endTime 响应到达时的本机时间戳
 * @param {boolean} force 是否强制重新校准（已有的基准可能已失效时使用）
 * @returns {boolean} 是否成功获得服务器时间基准
 */
export function syncClockFromResponse(dateHeader, startTime, endTime, force = false) {
  // 已校准且非强制：不再用普通响应反复覆盖基准
  if (calibrated && !force) return true;
  if (!dateHeader) return false;

  const serverMs = Date.parse(dateHeader);
  if (!Number.isFinite(serverMs)) return false;

  const end = Number.isFinite(endTime) ? endTime : Date.now();
  const start = Number.isFinite(startTime) ? startTime : end;
  const rtt = Math.max(0, end - start);

  // 服务器在「请求到达」时刻生成 Date，故服务器当前时间 ≈ Date + 500ms(截断中值) + rtt/2
  const estimatedServerNow = serverMs + 500 + rtt / 2;
  return apply(estimatedServerNow - end);
}

/**
 * 估计的服务器当前时间（毫秒）
 * @returns {number}
 */
export function getServerNow() {
  return Date.now() + skewMs;
}

/**
 * 估计的服务器当前时间戳（秒），用于 token 中的 v 字段
 * @returns {number}
 */
export function getServerTimestampSec() {
  return Math.floor((Date.now() + skewMs) / 1000);
}

/**
 * 当前使用的偏移量（毫秒，服务器时间 - 本机时间），便于排查问题
 * @returns {number}
 */
export function getClockSkewMs() {
  return skewMs;
}

/**
 * 是否已从服务器校准过
 * @returns {boolean}
 */
export function isClockCalibrated() {
  return calibrated;
}
